import React, {useEffect, useState} from 'react';
import {VideoPlayback} from '../../VideoPlayback';
import {CourseAccessGate} from '../../../modules/CourseAccessGate';
import {LessonPresentation} from '../../../data/models/Course/LessonPresentation';
import {useNavigate, useParams} from 'react-router-dom';
import {inject, observer} from 'mobx-react';
import CourseVideoPlaybackStore from '../store/CourseVideoPlaybackStore';
import {LearnerProgressModel} from '../../../data/models/LessonContent/CreateLearnerLessonModel';
import {LessonLearner} from '../../../data/models/LessonContent/LessonLearner';
import {parseLessonAnswers} from '../../VideoPlayback/lessonAnswersParset';
import {LessonType} from '../../../data/staticValues/lessonTypes';

interface Props {
  preview?: boolean;
  courseVideoPlaybackStore?: CourseVideoPlaybackStore;
}

const CourseVideoPlaybackContainer = inject('courseVideoPlaybackStore')(
  observer(({preview, courseVideoPlaybackStore}: Props) => {
    const [lessonPresentation, setLessonPresentation] =
      useState<LessonPresentation>();
    const [finishedLessons, setFinishedLessons] = useState<string[]>([]);

    const [progress, setProgress] = useState<LearnerProgressModel>();
    const [lessonLearner, setLessonLearner] = useState<LessonLearner>();
    const [nextLessonId, setNextLessonId] = useState<string>();

    const navigate = useNavigate();
    const {courseId, lessonId, organizationId} = useParams();

    useEffect(() => {
      if (!preview && courseId && lessonId) {
        courseVideoPlaybackStore!.createLessonLearner(courseId, {
          lessonId: lessonId,
        });
      }
    }, [finishedLessons]);

    useEffect(() => {
      initializaData();
    }, [courseVideoPlaybackStore, lessonId]);

    useEffect(() => {
      if (lessonPresentation && courseId && !preview) {
        courseVideoPlaybackStore!
          .getLearnerCourseProgress(courseId)
          .then(res => {
            if (
              res?.nextLessonId &&
              res?.nextLessonId ===
                lessonPresentation?.currentLesson.supplementalLessonId
            ) {
              navigateToSupplemental(res?.nextLessonId || '');
            }
          });
      }
    }, [lessonPresentation]);

    const initializaData = async () => {
      if (!courseId || !lessonId) return;
      setLessonPresentation(undefined);
      setLessonLearner(undefined);
      setProgress(undefined);
      if (preview && organizationId) {
        await courseVideoPlaybackStore!
          .getLessonPreview(organizationId, courseId, lessonId)
          .then(setLessonPresentation);
      } else {
        await courseVideoPlaybackStore!
          .getLessonPresentation(courseId, lessonId, [LessonType.MICROREEL])
          .then(setLessonPresentation);
      }
      await courseVideoPlaybackStore
        ?.getLearnerProgress(courseId, lessonId)
        .then(res => {
          setLessonLearner(res?.lessonLearner);
          setFinishedLessons(res?.finishedLessonIdsInCourse || []);
          setProgress(res?.progress);
        });
    };

    const onFinishedLesson = (body: LearnerProgressModel) => {
      if (courseId && lessonId) {
        courseVideoPlaybackStore
          ?.editLessonLearnerProgress(courseId, lessonId, body)
          .then(res => {
            setNextLessonId(res?.nextLessonId);
            setProgress(res?.progress);
            setLessonLearner(res?.lessonLearner);
          });
      }
    };

    const getGradesResult = () => {
      if (
        progress &&
        lessonLearner &&
        lessonPresentation &&
        lessonPresentation.currentLesson.isGradable &&
        lessonPresentation.currentLesson.passingScore !== undefined
      ) {
        const grade = parseLessonAnswers(
          progress,
          lessonPresentation.lessonContent,
          lessonLearner,
          lessonPresentation.currentLesson.passingScore,
        );
        return grade;
      }
      return undefined;
    };

    const navigateToSupplemental = (supplementalLessonId: string) => {
      navigate(`/course/${courseId}/lesson/${supplementalLessonId}`, {
        replace: true,
      });
    };

    const onContinue = () => {
      if (preview) {
        navigate(-1);
      } else if (nextLessonId) {
        if (nextLessonId === lessonId) {
          window.location.reload();
        } else {
          navigate(`/course/${courseId}/lesson/${nextLessonId}`, {
            replace: true,
          });
        }
      }
    };

    const onCourseHome = () => {
      if (preview) {
        navigate(-1);
      } else {
        navigate(`/course/${courseId}`, {replace: true});
      }
    };

    const onOpenLesson = (lessonId: string) => {
      navigate(`/course/${courseId}/lesson/${lessonId}`);
    };

    const navigateToLesson = async (navigateToLessonId: string) => {
      if (preview) {
        navigate(
          `/preview/${organizationId}/course/${courseId}/lesson/${navigateToLessonId}`,
        );
      } else if (lessonPresentation?.organization) {
        const publishedId =
          await courseVideoPlaybackStore!.getPublishedLessonId(
            lessonPresentation.organization.id,
            navigateToLessonId,
          );
        if (publishedId) {
          navigate(`/course/${courseId}/lesson/${publishedId}`);
        }
      }
    };

    const onBack = () => {
      navigate(-1);
    };

    const getPassingScore = () => {
      return lessonPresentation?.currentLesson?.passingScore;
    };

    return (
      <CourseAccessGate publishedCourse={!preview}>
        {lessonPresentation ? (
          <VideoPlayback
            presentation={lessonPresentation}
            currentLessonId={lessonId || ''}
            gradeResult={getGradesResult()}
            preview={preview}
            onBack={onBack}
            onContinue={onContinue}
            onCourseHome={onCourseHome}
            finishedLessons={finishedLessons}
            onOpenLesson={onOpenLesson}
            passingScore={getPassingScore()}
            isLoading={courseVideoPlaybackStore?.isLoading}
            nextLessonId={nextLessonId}
            onFinishedLesson={onFinishedLesson}
            navigateToLesson={navigateToLesson}
          />
        ) : null}
      </CourseAccessGate>
    );
  }),
);

export default CourseVideoPlaybackContainer;
